/**
 * Fronteira de servico dos dados de consorcio.
 *
 * getModelosConsorcio e passagem direta: o catalogo ja devolve o shape do
 * contrato, sem mapeamento.
 *
 * getHistoricoConsorcio so monta os parametros do filtro generico da API e
 * chama http; quem resolve as armadilhas do filtro (produto cartesiano
 * quando os meses atravessam o ano, mais de uma linha por colaborador na
 * mesma competencia) e mapearHistoricoConsorcio, em consorcioMapper.js — ver
 * o comentario la para o detalhe de cada regra.
 *
 * per_page: -1 e obrigatorio: sem ele a rota pagina e a media sai calculada
 * sobre a primeira pagina.
 */
import http from "./http";
import { mapearHistoricoConsorcio, mapearComissaoP2Consorcio } from "./consorcioMapper";

/**
 * Catalogo de modelos de veiculo que existem como cota de consorcio.
 *
 * per_page: -1 e fixo (nao sobrescrevivel por filtros): a rota pagina em 15
 * por default do Laravel (BaseController@index, nenhum model redefine
 * $perPage) e este catalogo so existe para popular um seletor — nunca faz
 * sentido vir truncado. Mesmo cuidado que getProdutoModelo ja toma no
 * componente; aqui fica garantido no servico, para ninguem esquecer de novo.
 * @param {{id_band?: number}} filtros
 */
export function getModelosConsorcio(filtros) {
  return http.get("/campanhas/modelo-consorcio", { params: { ...filtros, per_page: -1 } });
}

/**
 * Contagem de cotas P1 por colaborador e mes, para o calculo da media.
 *
 * Le /campanhas/dados-consorcio (a venda, linha a linha), nao
 * /campanhas/comissao-consorcio (a comissao, ja agregada). O bonus continua na
 * de comissao — sao dados diferentes: aqui se conta cota, la se soma dinheiro.
 *
 * id_tipo_dado: "R" (retroativo) e id_tipo_evento: "C" sao obrigatorios: a
 * mesma competencia (colaborador, ano_ref, mes_ref) tem mais de um fechamento
 * em vw_mcp_rel_comissao_consorcio, e sem esse par fixo a media soma o mensal
 * com o retroativo — a mesma venda contada duas vezes.
 *
 * "R" e nao "M" porque a media le meses JA FECHADOS. O bonus (P2) e o realizado
 * da apuracao continuam em "M": olham a competencia corrente. Ver
 * getComissaoP2Consorcio abaixo e o cursor SQLRealizadoModelo da procedure.
 * @param {{id_colaboradores: number[], meses: Array<{ano_ref, mes_ref}>}} payload
 */
export function getHistoricoConsorcio(payload) {
  const { id_colaboradores, meses } = payload || {};
  const idColaboradores = id_colaboradores || [];
  const mesesPedidos = meses || [];

  const anosPedidos = [...new Set(mesesPedidos.map(m => m.ano_ref))];
  const mesesRefPedidos = [...new Set(mesesPedidos.map(m => m.mes_ref))];

  return http
    .get("/campanhas/dados-consorcio", {
      params: {
        id_colaborador: idColaboradores.join(","),
        // "ano"/"mes" e nao "ano_ref"/"mes_ref": e assim que dados-consorcio
        // nomeia a competencia. A rota de comissao usa os nomes com _ref.
        ano: anosPedidos.join(","),
        mes: mesesRefPedidos.join(","),
        status: "P1",
        id_tipo_dado: "R",
        id_tipo_evento: "C",
        per_page: -1
      }
    })
    .then(response => ({
      ...response,
      data: {
        ...response.data,
        data: mapearHistoricoConsorcio(response.data.data, mesesPedidos, idColaboradores)
      }
    }));
}

/**
 * Comissao P2 (fechada) do colaborador numa unica competencia — usada pela
 * tabela de metas do participante para mostrar quanto o bonus por "% sobre
 * referencia" daria hoje sobre a comissao P2 daquele mes, antes de o
 * usuario decidir o percentual.
 *
 * status: "P2" porque aqui e a comissao de fato (valor_comissao), nao a
 * contagem de cotas (qtde) usada por getHistoricoConsorcio para a media.
 * id_tipo_dado: "M" e id_tipo_evento: "C" continuam obrigatorios pelo mesmo
 * motivo: a mesma competencia tem fechamento mensal e retroativo em
 * vw_mcp_rel_comissao_consorcio, e somar os dois conta a comissao duas
 * vezes — isso ja foi um bug. per_page: -1 obrigatorio, mesmo motivo:
 * sem ele a rota pagina e a soma sai calculada so sobre a primeira pagina.
 * @param {{id_colaboradores: number[], ano_ref, mes_ref}} payload
 */
export function getComissaoP2Consorcio(payload) {
  const { id_colaboradores, ano_ref, mes_ref } = payload || {};
  const idColaboradores = id_colaboradores || [];

  return http
    .get("/campanhas/comissao-consorcio", {
      params: {
        id_colaborador: idColaboradores.join(","),
        ano_ref,
        mes_ref,
        status: "P2",
        id_tipo_dado: "M",
        id_tipo_evento: "C",
        per_page: -1
      }
    })
    .then(response => ({
      ...response,
      data: {
        ...response.data,
        data: mapearComissaoP2Consorcio(response.data.data)
      }
    }));
}
